import React from 'react';
import { useTranslation } from 'react-i18next';
import { CATEGORIES } from '../../../data/categories';
import { FormSection } from './FormSection';

const categoryIdByName = new Map(CATEGORIES.map((c) => [c.name, c.id]));

export const subcategorySlug = (sub) =>
  sub.toLowerCase().replace(/[^a-z0-9]/g, '-');

// Slugs for adFields options/placeholders (labels use f.key directly).
const slugify = (s) =>
  (s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const optionSlug = (opt) => slugify(opt);
const placeholderSlug = (ph) => {
  let s = slugify(ph);
  if (/^e-g-/.test(s)) s = s.replace('e-g-', 'eg-');
  return s;
};

const SECTION_ICON = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export default function CategorySection({
  values,
  onChange,
  errors,
  categoriesList,
  subcategoriesList,
  categoryFields,
  handleChange,
  handleExtraChange,
  extra,
}) {
  const { t } = useTranslation();
  const categoryLabel = (cat) =>
    t(`categories.${categoryIdByName.get(cat.name) || cat.name}`, cat.name);
  const subcategoryLabel = (sub) =>
    t(`subcategories.${subcategorySlug(sub)}`, sub);
  return (
    <FormSection
      icon={SECTION_ICON}
      title={t('createAd.categoryAndDetails')}
      subtitle={t('createAd.chooseCategory')}
    >
      <div className="cf-row">
        <div className="cf-field cf-field--half">
          <label htmlFor="ad-category">
            {t('createAd.category')} <span className="cf-req">*</span>
          </label>
          <select
            id="ad-category"
            name="category"
            value={values.category}
            onChange={(e) => {
              onChange('category', e.target.value);
              onChange('subcategory', '');
            }}
            className={`cf-input${errors.category ? ' cf-input--error' : ''}`}
          >
            <option value="">{t('createAd.selectCategory')}</option>
            {categoriesList.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {categoryLabel(cat)}
              </option>
            ))}
          </select>
          {errors.category && (
            <span className="cf-error">{errors.category}</span>
          )}
        </div>
        {subcategoriesList.length > 0 && (
          <div className="cf-field cf-field--half">
            <label htmlFor="ad-subcategory">{t('createAd.subcategory')}</label>
            <select
              id="ad-subcategory"
              name="subcategory"
              value={values.subcategory}
              onChange={handleChange('subcategory')}
              className="cf-input"
            >
              <option value="">{t('createAd.selectSubcategory')}</option>
              {subcategoriesList.map((sub) => (
                <option key={sub} value={sub}>
                  {subcategoryLabel(sub)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {categoryFields.length > 0 && (
        <div className="cf-category-details">
          <span className="cf-category-details-label">
            {t('createAd.categoryDetails', {
              category: categoryLabel({
                name: values.category,
                id: categoryIdByName.get(values.category),
              }),
            })}
          </span>
          <div className="cf-row cf-row--wrap">
            {categoryFields.map((f) => (
              <div key={f.key} className="cf-field cf-field--half">
                <label htmlFor={`ad-${f.key}`}>
                  {t(`adFields.${f.key}`, { defaultValue: f.label })}
                  {f.required && !f.label.includes('*') && (
                    <span className="cf-req"> *</span>
                  )}
                </label>
                {f.type === 'select' && (
                  <select
                    id={`ad-${f.key}`}
                    className={`cf-input${errors[`extra.${f.key}`] ? ' cf-input--error' : ''}`}
                    value={extra[f.key] || ''}
                    onChange={handleExtraChange(f.key)}
                  >
                    <option value="">
                      {t('createAd.select', { label: t(`adFields.${f.key}`, { defaultValue: f.label }).replace(/\s*\*/, '').toLowerCase() })}
                    </option>
                    {(f.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {t(`adFieldOptions.${optionSlug(opt)}`, opt)}
                      </option>
                    ))}
                  </select>
                )}
                {f.type === 'checkbox' && (
                  <div className="cf-checkbox">
                    <input
                      id={`ad-${f.key}`}
                      type="checkbox"
                      checked={!!extra[f.key]}
                      onChange={handleExtraChange(f.key)}
                    />
                    <label htmlFor={`ad-${f.key}`}>
                      {t(`adFields.${f.key}`, { defaultValue: f.label }).replace(/\s*\*/, '')}
                    </label>
                  </div>
                )}
                {f.type !== 'select' && f.type !== 'checkbox' && (
                  <input
                    id={`ad-${f.key}`}
                    className={`cf-input${errors[`extra.${f.key}`] ? ' cf-input--error' : ''}`}
                    type={f.type}
                    value={extra[f.key] || ''}
                    onChange={handleExtraChange(f.key)}
                    placeholder={t(`adFieldPlaceholders.${placeholderSlug(f.placeholder)}`, { defaultValue: f.placeholder })}
                    min={f.min}
                    max={f.max}
                  />
                )}
                {errors[`extra.${f.key}`] && (
                  <span className="cf-error">{errors[`extra.${f.key}`]}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </FormSection>
  );
}
